import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  ARAutomationByCampaignTotalAmountModel,
  ARAutomationByCollectionModel,
  ARAutomationCampaignsReqModel,
} from 'src/app/models/tenants/ar-dasboard.models';
import { ApiService } from '..';
import { CONTROLLERS, ENDPOINTS } from '../../constants';
import {
  ClearentCustomer,
  EditPreviewPaymentPlan,
  GetPaymentPlanEMISchedulesInfoResponse,
  PaymentPlan,
  PaymentPlanPatientResp,
  PaymentPlanReportReqCommand,
  PaymentPlanReportResp,
  PaymentPlanTemplate,
  PaymentPlanTemplateList,
  Response,
  SendPaymentPlanReq,
  UpdatePaymentPlanInitialAmount,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class PaymentPlanService {
  private api: string = CONTROLLERS.PAYMENTS.PAYMENT_PLAN;
  constructor(private apiService: ApiService) {}

  getClearentCustomerByPatientId(
    patientId: number
  ): Observable<Response<ClearentCustomer>> {
    return this.apiService.getById(
      patientId.toString(),
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_CLEARENT_CUSTOMER_BY_PATIENT_ID}`
    );
  }

  createClearentCustomer(body: ClearentCustomer): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.CREATE_CLEARENT_CUSTOMER}`,
      body
    );
  }

  createUpdatePaymentPlan(formData: FormData): Observable<Response<any>> {
    return this.apiService.postMultipartFormData(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.CREATE_UPDATE_PAYMENT_PLAN}`,
      formData
    );
  }

  // Task 3591. Takes back a payment plan that was just created when its initial payment was not completed.
  discardUnpaidPaymentPlan(
    paymentPlanId: number,
    practiceId: number
  ): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.DISCARD_UNPAID_PAYMENT_PLAN}`,
      { paymentPlanId, practiceId }
    );
  }

  adjustPaymentPlan(formData: FormData): Observable<Response<any>> {
    return this.apiService.postMultipartFormData(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.ADJUST_PAYMENT_PLAN}`,
      formData
    );
  }

  updatePaymentPlanInitialAmount(
    body: UpdatePaymentPlanInitialAmount
  ): Observable<Response<any>> {
    return this.apiService.put(
      '',
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.UPDATE_PAYMENT_PLAN_INITIAL_AMOUNT}`,
      body
    );
  }

  getPaymentPlanTemplatesByPracticeId(
    practiceId: number
  ): Observable<Response<PaymentPlanTemplate[]>> {
    return this.apiService.getById(
      practiceId.toString(),
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLAN_TEMPLATES}`
    );
  }

  getPaymentPlanTemplatesList(
    practiceId: number,
    pageNumber: number,
    pageSize: number,
    searchTerm: string
  ): Observable<Response<PaymentPlanTemplateList>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLAN_TEMPLATES_LIST}`,
      {
        practiceId,
        pageNumber,
        pageSize,
        searchTerm,
      }
    );
  }

  createPaymentPlanTemplate(
    body: PaymentPlanTemplate
  ): Observable<Response<any>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.CREATE_PAYMENT_PLAN_TEMPLATE}`,
      body
    );
  }

  updatePaymentPlanTemplate(
    body: PaymentPlanTemplate
  ): Observable<Response<any>> {
    return this.apiService.put(
      '',
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.UPDATE_PAYMENT_PLAN_TEMPLATE}`,
      body
    );
  }

  getPaymentPlansByPatientId(
    practiceId: number,
    patientId: number,
    pageNumber: number,
    pageSize: number
  ): Observable<Response<PaymentPlanPatientResp>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLANS}`,
      {
        practiceId,
        patientId,
        pageNumber,
        pageSize,
      }
    );
  }

  getPaymentPlansByPracticeId(
    practiceId: number,
    pageNumber: number,
    pageSize: number,
    customerId: number,
    searchTerm: string = '',
    sortBy: number = 2,
    amountDue: number | null = null,
    amountOperator: number = 1,
    isOpenInstallments: boolean = false,
    patientId: string = '',
    patientName: string = '',
    paymentPlanName: string = '',
    paymentPlanStatuses: any[] | null = null,
    templateName: string = ''
  ): Observable<Response<PaymentPlanPatientResp>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLANS_FOR_PRACTICE}`,
      {
        practiceId,
        pageNumber,
        pageSize,
        customerId,
        searchTerm,
        sortBy,
        amountDue,
        amountOperator,
        isOpenInstallments,
        patientId,
        patientName,
        paymentPlanName,
        paymentPlanStatuses,
        templateName,
      }
    );
  }

  getPaymentPlanReportList(
    body: PaymentPlanReportReqCommand
  ): Observable<Response<PaymentPlanReportResp>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYEMNT_PLAN_REPORT}`,
      body
    );
  }

  getPaymentPlanByPaymentPlanId(
    paymentPlanId: number
  ): Observable<Response<PaymentPlan>> {
    return this.apiService.getById(
      paymentPlanId.toString(),
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLAN}`
    );
  }

  getEditPaymentPlanByPaymentPlanId(
    paymentPlanId: number
  ): Observable<Response<EditPreviewPaymentPlan>> {
    return this.apiService.getById(
      paymentPlanId.toString(),
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLAN}`
    );
  }

  getARAutomationCampingAmount(
    arAutomationCampaignsReqModel: ARAutomationCampaignsReqModel
  ): Observable<Response<ARAutomationByCampaignTotalAmountModel>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_AUTOMATION_CAMPIGN_TOTALAMOUNT}`,
      arAutomationCampaignsReqModel
    );
  }

  getARAutomationCollection(
    arAutomationCampaignsReqModel: ARAutomationCampaignsReqModel
  ): Observable<Response<ARAutomationByCollectionModel[]>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_AUTOMATION_COLLECTION}`,
      arAutomationCampaignsReqModel
    );
  }

  sendPaymentPlanEmail(
    sendPaymentPlanReq: SendPaymentPlanReq
  ): Observable<Response<string>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.SEND_PAYMENT_PLAN_EMAIL}`,
      sendPaymentPlanReq
    );
  }

  getPaymentPlanEMIByVersion(
    paymentPlanId: number,
    emiVersion: number,
    customerId: number,
    practiceId: number
  ): Observable<Response<EditPreviewPaymentPlan>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLAN_EMIS_BY_VERSION}`,
      {
        paymentPlanId,
        emiVersion,
        customerId,
        practiceId,
      }
    );
  }

  GetPaymentPlanEMISchedulesDropdown(
    paymentPlanId: number,
    customerId: number,
    practiceId: number
  ): Observable<Response<GetPaymentPlanEMISchedulesInfoResponse>> {
    return this.apiService.post(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.GET_PAYMENT_PLAN_SCHEDULE_INFO}`,
      {
        paymentPlanId,
        customerId,
        practiceId,
      }
    );
  }

  uploadSignedContract(formData: FormData): Observable<Response<any>> {
    return this.apiService.postMultipartFormData(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.UPLOAD_SIGNED_CONTRACT}`,
      formData
    );
  }

    paymentPlanDownloadPDF(practiceId: number, customerId: number,patientId: number, genId: string, paymentPlan: string) : Observable<Blob>{
    return this.apiService.postBlob(
      `${this.api}/${ENDPOINTS.PAYMENTS.PAYMENT_PLAN.PAYMENT_PLAN_DOWNLOAD}`,
      { practiceId, customerId,patientId, genId, paymentPlan}
    );
  }
}
